import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { kiAnalyseApi, kuendigungenApi } from '../api';

// ─── Konstanten ───────────────────────────────────────────────────────────────

const TABS = [
  { id: 'zusammenfassung', label: 'Zusammenfassung' },
  { id: 'klauseln', label: 'Klauselanalyse' },
  { id: 'preisaenderungen', label: 'Preisänderungen' },
  { id: 'sonderkuendigung', label: 'Sonderkündigung' },
  { id: 'kuendigung_starten', label: 'Kündigung starten' },
];

const KLAUSEL_TYP_LABELS = {
  kuendigung: 'Kündigung',
  preisanpassung: 'Preisanpassung',
  laufzeit: 'Laufzeit',
  haftung: 'Haftung',
  datenschutz: 'Datenschutz',
  sonstiges: 'Sonstige',
};

const SICHERHEITS_LABELS = {
  regelbasiert: 'Regelbasiert',
  dokumentbasiert: 'Aus Dokument',
  ki: 'KI-Analyse',
};

const RISIKO_FARBE = {
  niedrig: '#22c55e',
  mittel: '#eab308',
  hoch: '#ef4444',
};

// ─── Hilfsfunktionen ──────────────────────────────────────────────────────────

function formatDE(datum) {
  if (!datum) return '–';
  try {
    return new Date(datum).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return String(datum);
  }
}

function formatEuro(v) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(v) || 0);
}

// ─── Kleine Hilfskomponenten ──────────────────────────────────────────────────

function RisikoBadge({ risiko }) {
  const farbe = RISIKO_FARBE[risiko] || '#64748b';
  return (
    <span style={{ background: farbe + '22', color: farbe, border: `1px solid ${farbe}55`, borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
      {risiko || 'unbekannt'}
    </span>
  );
}

function CacheBadge({ ausCache }) {
  if (!ausCache) return null;
  return (
    <span style={{ background: '#0e7490', color: '#e0f2fe', border: '1px solid #0891b2', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
      Aus Cache
    </span>
  );
}

function ProviderInfo({ provider, analysiert_am, aus_cache }) {
  if (!provider && !analysiert_am && !aus_cache) return null;
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', fontSize: 12, color: '#64748b' }}>
      {provider && <span>Provider: <strong style={{ color: '#94a3b8' }}>{provider}</strong></span>}
      {analysiert_am && <span>Analysiert: <strong style={{ color: '#94a3b8' }}>{formatDE(analysiert_am)}</strong></span>}
      <CacheBadge ausCache={aus_cache} />
    </div>
  );
}

function EmptyHint({ text }) {
  return (
    <div style={{ color: '#64748b', background: '#0f172a', border: '1px dashed #334155', borderRadius: 8, padding: 20, fontSize: 14, textAlign: 'center' }}>
      {text}
    </div>
  );
}

function TabShell({ loading, error, onRefresh, children }) {
  if (loading) {
    return <div style={{ color: '#6366f1', padding: 48, textAlign: 'center', fontSize: 15 }}>KI-Analyse wird geladen…</div>;
  }
  if (error) {
    return (
      <div>
        <div style={{ background: '#431407', border: '1px solid #ef4444', color: '#f87171', borderRadius: 10, padding: 14, marginBottom: 12, fontSize: 14 }}>
          {error}
        </div>
        <button onClick={onRefresh} className="btn-secondary">Erneut versuchen</button>
      </div>
    );
  }
  return <>{children}</>;
}

// ─── Tab: Zusammenfassung ─────────────────────────────────────────────────────

function ZusammenfassungTab({ data, loading, error, onRefresh }) {
  return (
    <TabShell loading={loading} error={error} onRefresh={onRefresh}>
      {data && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
            <ProviderInfo provider={data.provider} analysiert_am={data.analysiert_am} aus_cache={data.aus_cache} />
            <button onClick={onRefresh} className="btn-secondary">↻ Aktualisieren</button>
          </div>

          <div style={innerCard}>
            <div style={{ color: '#cbd5e1', fontSize: 14, lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
              {data.zusammenfassung || 'Keine Zusammenfassung verfügbar.'}
            </div>
          </div>

          {data.quellen?.length > 0 && (
            <div style={{ marginTop: 18 }}>
              <div style={sectionLabel}>Quellen</div>
              <div style={{ display: 'grid', gap: 8 }}>
                {data.quellen.map((q, i) => (
                  <div key={i} style={innerCard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
                      <span style={{ color: '#38bdf8', fontSize: 12, fontWeight: 700 }}>
                        {q.quelle || 'Unbekannte Quelle'}
                      </span>
                      {q.relevanz != null && (
                        <span style={{ color: '#64748b', fontSize: 11 }}>
                          Relevanz: {Math.round((q.relevanz || 0) * 100)} %
                        </span>
                      )}
                    </div>
                    <div style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.6 }}>{q.text}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </TabShell>
  );
}

// ─── Tab: Klauselanalyse ──────────────────────────────────────────────────────

function KlauselnTab({ data, loading, error, onRefresh }) {
  return (
    <TabShell loading={loading} error={error} onRefresh={onRefresh}>
      {data && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
            <ProviderInfo provider={data.provider} aus_cache={data.aus_cache} />
            <button onClick={onRefresh} className="btn-secondary">↻ Aktualisieren</button>
          </div>

          {!data.klauseln?.length ? (
            <EmptyHint text="Keine kritischen Klauseln gefunden." />
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              {data.klauseln.map((k, i) => (
                <div key={i} style={innerCard}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={typBadge}>
                        {KLAUSEL_TYP_LABELS[k.typ] || k.typ || 'Sonstige'}
                      </span>
                      <RisikoBadge risiko={k.risiko} />
                    </div>
                    {k.fundstelle && (
                      <span style={{ color: '#64748b', fontSize: 11 }}>{k.fundstelle}</span>
                    )}
                  </div>
                  <div style={{ color: '#f1f5f9', fontSize: 14, lineHeight: 1.7, marginBottom: k.erklaerung ? 10 : 0 }}>
                    {k.text}
                  </div>
                  {k.erklaerung && (
                    <div style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.6, borderTop: '1px solid #334155', paddingTop: 10 }}>
                      {k.erklaerung}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </TabShell>
  );
}

// ─── Tab: Preisänderungen ─────────────────────────────────────────────────────

function PreisaenderungenTab({ data, loading, error, onRefresh }) {
  return (
    <TabShell loading={loading} error={error} onRefresh={onRefresh}>
      {data && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
            <button onClick={onRefresh} className="btn-secondary">↻ Aktualisieren</button>
          </div>

          {(data.aktueller_preis != null || data.zahlungsintervall) && (
            <div style={{ background: '#172033', border: '1px solid #1d4ed8', borderRadius: 10, padding: 16, marginBottom: 18 }}>
              <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 4 }}>Aktueller Preis</div>
              <div style={{ color: '#f8fafc', fontSize: 24, fontWeight: 800 }}>
                {data.aktueller_preis != null ? formatEuro(data.aktueller_preis) : '–'}
                {data.zahlungsintervall && (
                  <span style={{ color: '#64748b', fontSize: 14, fontWeight: 400, marginLeft: 8 }}>
                    / {data.zahlungsintervall}
                  </span>
                )}
              </div>
            </div>
          )}

          {data.preishistorie?.length > 0 && (
            <div style={{ marginBottom: 22 }}>
              <div style={sectionLabel}>Preishistorie</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Datum</th>
                      <th style={thStyle}>Alter Preis</th>
                      <th style={thStyle}>Neuer Preis</th>
                      <th style={thStyle}>Steigerung %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.preishistorie.map((h, i) => {
                      const steigerung = h.steigerung_prozent;
                      const steigerungFarbe = steigerung > 0 ? '#f87171' : steigerung < 0 ? '#34d399' : '#94a3b8';
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid #1e293b' }}>
                          <td style={tdStyle}>{formatDE(h.datum)}</td>
                          <td style={tdStyle}>{formatEuro(h.kosten_alt)}</td>
                          <td style={{ ...tdStyle, fontWeight: 700, color: '#f8fafc' }}>
                            {formatEuro(h.kosten_neu)}
                          </td>
                          <td style={{ ...tdStyle, color: steigerungFarbe, fontWeight: 700 }}>
                            {steigerung != null
                              ? `${steigerung > 0 ? '+' : ''}${Number(steigerung).toFixed(1)} %`
                              : '–'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {data.erkannt_in_dokumenten?.length > 0 && (
            <div>
              <div style={sectionLabel}>Erkannte Preisänderungen in Dokumenten</div>
              <div style={{ display: 'grid', gap: 10 }}>
                {data.erkannt_in_dokumenten.map((e, i) => (
                  <div key={i} style={innerCard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
                      <span style={{ color: '#f8fafc', fontWeight: 700, fontSize: 15 }}>
                        {e.betrag_alt != null && `${formatEuro(e.betrag_alt)} → `}
                        <span style={{ color: '#38bdf8' }}>{formatEuro(e.betrag_neu)}</span>
                      </span>
                      <span style={{ color: '#64748b', fontSize: 12 }}>{formatDE(e.datum)}</span>
                    </div>
                    {e.hinweis && (
                      <div style={{ color: '#cbd5e1', fontSize: 13, marginBottom: 4 }}>{e.hinweis}</div>
                    )}
                    {e.fundstelle && (
                      <div style={{ color: '#64748b', fontSize: 11 }}>{e.fundstelle}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!data.preishistorie?.length && !data.erkannt_in_dokumenten?.length && (
            <EmptyHint text="Keine Preisänderungen erfasst oder in Dokumenten erkannt." />
          )}
        </>
      )}
    </TabShell>
  );
}

// ─── Tab: Sonderkündigung ─────────────────────────────────────────────────────

function SonderkuendigungTab({ data, loading, error, onRefresh }) {
  return (
    <TabShell loading={loading} error={error} onRefresh={onRefresh}>
      {data && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
            <ProviderInfo provider={data.provider} />
            <button onClick={onRefresh} className="btn-secondary">↻ Aktualisieren</button>
          </div>

          {/* Disclaimer immer prominent anzeigen */}
          {data.disclaimer && (
            <div style={disclaimerBox}>
              <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 6 }}>Rechtlicher Hinweis</div>
              <div style={{ fontSize: 13, lineHeight: 1.7 }}>{data.disclaimer}</div>
            </div>
          )}

          {!data.hinweise?.length ? (
            <EmptyHint text="Keine Sonderkündigungshinweise gefunden." />
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              {data.hinweise.map((h, i) => (
                <div key={i} style={innerCard}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                    <h4 style={{ color: '#f8fafc', fontSize: 15, fontWeight: 700, margin: 0 }}>
                      {h.grund}
                    </h4>
                    {h.sicherheit && (
                      <span style={{ color: '#94a3b8', fontSize: 11, background: '#1e293b', border: '1px solid #334155', borderRadius: 6, padding: '2px 8px', whiteSpace: 'nowrap' }}>
                        {SICHERHEITS_LABELS[h.sicherheit] || h.sicherheit}
                      </span>
                    )}
                  </div>
                  {h.frist && (
                    <div style={{ color: '#eab308', fontSize: 13, marginBottom: 8 }}>
                      Frist: <strong>{h.frist}</strong>
                    </div>
                  )}
                  {h.hinweis && (
                    <div style={{ color: '#cbd5e1', fontSize: 14, lineHeight: 1.7, marginBottom: 8 }}>
                      {h.hinweis}
                    </div>
                  )}
                  {h.rechtsgrundlage && (
                    <div style={{ color: '#38bdf8', fontSize: 12, marginBottom: 4 }}>
                      Rechtsgrundlage: <span style={{ color: '#94a3b8' }}>{h.rechtsgrundlage}</span>
                    </div>
                  )}
                  {h.fundstelle && (
                    <div style={{ color: '#64748b', fontSize: 11 }}>{h.fundstelle}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </TabShell>
  );
}

// ─── Tab: Kündigung starten ───────────────────────────────────────────────────

function KuendigungStartenTab({ vertragId, navigate }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleStart = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await kuendigungenApi.vorgaenge.create({ vertrag_id: vertragId });
      navigate(`/kuendigungen/vorgaenge/${res.data.vorgang.id}`);
    } catch (e) {
      setError(e.response?.data?.error || 'Kündigungsvorgang konnte nicht angelegt werden.');
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 520, margin: '0 auto' }}>
      <div style={innerCard}>
        <h3 style={{ color: '#f8fafc', fontSize: 18, fontWeight: 800, margin: '0 0 10px' }}>
          Kündigungsvorgang anlegen
        </h3>
        <p style={{ color: '#94a3b8', fontSize: 14, lineHeight: 1.7, margin: '0 0 18px' }}>
          Lege einen neuen Kündigungsvorgang für diesen Vertrag an. Du kannst anschließend
          Kündigungsschreiben generieren, Nachweise hochladen und den gesamten Verlauf dokumentieren.
        </p>

        <div style={{ background: '#111827', border: '1px solid #263244', borderRadius: 8, padding: 12, marginBottom: 18 }}>
          <div style={{ color: '#64748b', fontSize: 12, marginBottom: 4 }}>Vertrag-ID</div>
          <div style={{ color: '#38bdf8', fontWeight: 700, fontSize: 16 }}>{vertragId}</div>
        </div>

        {error && (
          <div style={{ background: '#431407', border: '1px solid #ef4444', color: '#f87171', borderRadius: 8, padding: 12, marginBottom: 14, fontSize: 13 }}>
            {error}
          </div>
        )}

        <button
          onClick={handleStart}
          disabled={loading}
          style={{
            background: loading ? '#1e293b' : '#818cf8',
            color: loading ? '#64748b' : '#fff',
            border: 'none',
            borderRadius: 10,
            padding: '13px 24px',
            fontSize: 15,
            fontWeight: 800,
            cursor: loading ? 'not-allowed' : 'pointer',
            width: '100%',
            transition: 'background 0.15s',
          }}
        >
          {loading ? 'Vorgang wird angelegt…' : 'Kündigungsvorgang starten'}
        </button>
      </div>

      <div style={{ color: '#475569', fontSize: 12, marginTop: 12, textAlign: 'center', lineHeight: 1.6 }}>
        Du wirst nach dem Anlegen direkt zum neuen Vorgang weitergeleitet.
      </div>
    </div>
  );
}

// ─── Hauptkomponente ──────────────────────────────────────────────────────────

export default function KuendigungsAssistentPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('zusammenfassung');
  const [tabData, setTabData] = useState({});
  const [tabLoading, setTabLoading] = useState({});
  const [tabError, setTabError] = useState({});
  const loadedRef = useRef(new Set());

  const loadTab = async (tabId, forceRefresh = false) => {
    if (tabId === 'kuendigung_starten') return;
    setTabLoading(prev => ({ ...prev, [tabId]: true }));
    setTabError(prev => ({ ...prev, [tabId]: null }));
    try {
      let res;
      if (tabId === 'zusammenfassung') {
        res = await kiAnalyseApi.zusammenfassung(id, forceRefresh);
      } else if (tabId === 'klauseln') {
        res = await kiAnalyseApi.klauseln(id, forceRefresh);
      } else if (tabId === 'preisaenderungen') {
        res = await kiAnalyseApi.preisaenderungen(id);
      } else if (tabId === 'sonderkuendigung') {
        res = await kiAnalyseApi.sonderkuendigung(id);
      }
      setTabData(prev => ({ ...prev, [tabId]: res.data }));
      loadedRef.current.add(tabId);
    } catch (e) {
      setTabError(prev => ({
        ...prev,
        [tabId]: e.response?.data?.error || 'Analyse konnte nicht geladen werden.',
      }));
    } finally {
      setTabLoading(prev => ({ ...prev, [tabId]: false }));
    }
  };

  // Lazy-Loading: Tab wird geladen wenn er erstmalig aktiv wird
  useEffect(() => {
    if (!loadedRef.current.has(activeTab) && activeTab !== 'kuendigung_starten') {
      loadTab(activeTab);
    }
  }, [activeTab]); // eslint-disable-line

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const handleRefresh = (tabId) => {
    loadedRef.current.delete(tabId);
    // Zusammenfassung und Klauseln unterstützen serverseitigen Force-Refresh
    const forceRefresh = tabId === 'zusammenfassung' || tabId === 'klauseln';
    loadTab(tabId, forceRefresh);
  };

  return (
    <div style={page}>
      {/* Kopfzeile */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={() => navigate(-1)} className="btn-ghost">← Zurück</button>
        <div style={{ flex: 1 }} />
        <div style={{ color: '#64748b', fontSize: 13 }}>Vertrag {id}</div>
      </div>

      {/* Hero */}
      <div style={heroCard}>
        <div style={{ color: '#38bdf8', fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
          KI-Vertragsassistent
        </div>
        <h1 style={{ color: '#f8fafc', fontSize: 26, margin: '0 0 6px', fontWeight: 800, lineHeight: 1.2 }}>
          Kündigungsassistent
        </h1>
        <p style={{ color: '#94a3b8', margin: 0, fontSize: 14, lineHeight: 1.6 }}>
          KI-gestützte Analyse – Zusammenfassung, Klauselrisiken, Preisentwicklung und Sonderkündigungsoptionen.
        </p>
      </div>

      {/* Tab-Leiste */}
      <div style={tabBar}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            style={tabButton(activeTab === tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab-Inhalt */}
      <div style={tabContent}>
        {activeTab === 'zusammenfassung' && (
          <ZusammenfassungTab
            data={tabData.zusammenfassung}
            loading={!!tabLoading.zusammenfassung}
            error={tabError.zusammenfassung}
            onRefresh={() => handleRefresh('zusammenfassung')}
          />
        )}
        {activeTab === 'klauseln' && (
          <KlauselnTab
            data={tabData.klauseln}
            loading={!!tabLoading.klauseln}
            error={tabError.klauseln}
            onRefresh={() => handleRefresh('klauseln')}
          />
        )}
        {activeTab === 'preisaenderungen' && (
          <PreisaenderungenTab
            data={tabData.preisaenderungen}
            loading={!!tabLoading.preisaenderungen}
            error={tabError.preisaenderungen}
            onRefresh={() => handleRefresh('preisaenderungen')}
          />
        )}
        {activeTab === 'sonderkuendigung' && (
          <SonderkuendigungTab
            data={tabData.sonderkuendigung}
            loading={!!tabLoading.sonderkuendigung}
            error={tabError.sonderkuendigung}
            onRefresh={() => handleRefresh('sonderkuendigung')}
          />
        )}
        {activeTab === 'kuendigung_starten' && (
          <KuendigungStartenTab vertragId={id} navigate={navigate} />
        )}
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const page = { padding: 18, maxWidth: 1000, margin: '0 auto', display: 'grid', gap: 16 };
const heroCard = {
  background: 'linear-gradient(135deg, #172033 0%, #1e293b 65%, #10253d 100%)',
  border: '1px solid #334155',
  borderRadius: 12,
  padding: 22,
  boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
};
const tabBar = { display: 'flex', gap: 8, overflowX: 'auto', padding: '4px 0 2px', flexWrap: 'nowrap' };
const tabButton = (active) => ({
  flex: '0 0 auto',
  background: active ? '#818cf8' : '#1e293b',
  color: active ? '#fff' : '#cbd5e1',
  border: active ? '1px solid #818cf8' : '1px solid #334155',
  padding: '9px 14px',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: active ? 700 : 500,
});
const tabContent = {
  background: '#1e293b',
  border: '1px solid #334155',
  borderRadius: 12,
  padding: 20,
  minHeight: 320,
};
const innerCard = {
  background: '#0f172a',
  border: '1px solid #263244',
  borderRadius: 10,
  padding: 16,
};
const sectionLabel = {
  color: '#64748b',
  fontSize: 11,
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  marginBottom: 10,
};
const typBadge = {
  background: '#1d4ed822',
  color: '#60a5fa',
  border: '1px solid #1d4ed844',
  borderRadius: 6,
  padding: '2px 8px',
  fontSize: 11,
  fontWeight: 700,
};
const disclaimerBox = {
  background: '#431407',
  border: '2px solid #ef4444',
  color: '#fca5a5',
  borderRadius: 10,
  padding: 16,
  marginBottom: 16,
};
const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  minWidth: 400,
  fontSize: 14,
  background: '#0f172a',
  borderRadius: 8,
  overflow: 'hidden',
};
const thStyle = {
  background: '#0a0f1a',
  color: '#64748b',
  fontWeight: 700,
  fontSize: 11,
  textTransform: 'uppercase',
  padding: '10px 14px',
  textAlign: 'left',
  borderBottom: '1px solid #1e293b',
};
const tdStyle = {
  color: '#cbd5e1',
  padding: '10px 14px',
  verticalAlign: 'middle',
  borderBottom: '1px solid #111827',
};
